// CJEAsistencia.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AppBar,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseIcon from "@mui/icons-material/Close";
import CachedIcon from "@mui/icons-material/Cached";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
    DateCalendar,
    DayCalendarSkeleton,
    PickersDay,
} from "@mui/x-date-pickers";
import { es as esLocale } from "date-fns/locale/es";
import Fuse from "fuse.js";
import {
    NinioCard,
    PhotoCropDialog,
    CreateNinioDialog,
    EditNinioDialog,
    NinioInfoDialog,
    NinioPickerDialog,
} from "../components/CJE/NinioCardComponents.jsx";
import {
    supabase,
    todayDateOnly,
    formatDateOnly,
    parseYMD,
    readAll,
    withPhotos,
} from "../components/CJE/cjeApi";
import logoCJE from "../imgs/LOGOCJETRANSPARENTE.png";

const ServerDay = ({
    day,
    outsideCurrentMonth,
    monthCounts = {},
    ...other
}) => (
    <Box sx={{ position: "relative" }}>
        <PickersDay
            {...other}
            day={day}
            outsideCurrentMonth={outsideCurrentMonth}
        />
        {!outsideCurrentMonth && !!monthCounts[formatDateOnly(day)] && (
            <Box
                sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    px: 0.5,
                    borderRadius: 7,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: 1,
                    bgcolor: "success.main",
                    color: "common.white",
                    boxShadow: 1,
                    pointerEvents: "none",
                    outline: "1px solid",
                    outlineColor: "background.paper",
                }}
            >
                {monthCounts[formatDateOnly(day)]}
            </Box>
        )}
    </Box>
);

const CJEAsistencia = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const correctPassword = "A nuestro ritmo";

    const [ninios, setNinios] = useState([]);
    const [options, setOptions] = useState([]);
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [filtro, setFiltro] = useState("todos");
    const [expandA, setExpandA] = useState(true);
    const [expandB, setExpandB] = useState(true);

    const [dia, setDia] = useState(todayDateOnly);
    const calendarDate = useMemo(() => parseYMD(dia), [dia]);
    const [calendarMonth, setCalendarMonth] = useState(() =>
        todayDateOnly().slice(0, 7),
    );
    const [monthCounts, setMonthCounts] = useState({});
    const [loadingMonth, setLoadingMonth] = useState(false);
    const [monthError, setMonthError] = useState("");
    const [asistencia, setAsistencia] = useState({ dia: null, ids: [] });
    const [loadingDay, setLoadingDay] = useState(true);
    const [dayError, setDayError] = useState("");

    const [version, setVersion] = useState(0);
    const [revision, setRevision] = useState(0);
    const [realtimeError, setRealtimeError] = useState(false);
    const [savingIds, setSavingIds] = useState([]);
    const saving = useRef(new Set());

    const [createOpen, setCreateOpen] = useState(false);
    const [recoverTarget, setRecoverTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [infoTarget, setInfoTarget] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [linkTarget, setLinkTarget] = useState(null);
    const [qrOpen, setQrOpen] = useState(false);
    const [editOtherOpen, setEditOtherOpen] = useState(false);
    const [editOtherTemp, setEditOtherTemp] = useState(() =>
        parseYMD(todayDateOnly()),
    );

    const [photoTarget, setPhotoTarget] = useState(null);
    const [rawFile, setRawFile] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);
    const fileInputRef = useRef(null);
    const photoPath = useRef(null);
    const [toast, setToast] = useState({
        open: false,
        msg: "",
        sev: "success",
    });

    const curso = cursos.find(
        (c) => dia >= c.inicio && (!c.fin || dia <= c.fin),
    );
    const qr =
        qrOpen === "instagram"
            ? {
                  titulo: (
                      <Stack direction="row" alignItems="center" spacing={1}>
                          <InstagramIcon /> Instagram · CJE
                      </Stack>
                  ),
                  url: "https://www.instagram.com/cjencuentro",
              }
            : qrOpen === "whatsapp"
              ? {
                    titulo: (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <WhatsAppIcon /> WhatsApp · CJE
                        </Stack>
                    ),
                    url: "https://chat.whatsapp.com/KeoK8T7px55LG6M0V1MndX",
                }
              : {
                    titulo: (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <QrCode2Icon /> Formulario de inscripción · $
                            {curso?.id}
                        </Stack>
                    ),
                    url: curso?.formulario_url,
                };
    const ready = !loading && !error && !dayError && asistencia.dia === dia;

    useEffect(() => {
        if (!authenticated) return;
        let cancelled = false;
        setLoading(true);
        setError("");

        (async () => {
            try {
                const [cs, children, rows] = await Promise.all([
                    readAll(
                        supabase.from("CJECursos").select("*").order("inicio"),
                    ),
                    readAll(
                        supabase
                            .from("CJENinios")
                            .select(
                                "id,nombre,foto_path,telefono,padres,alergias_medicinas,origen",
                            )
                            .order("nombre")
                            .order("id"),
                    ),
                    readAll(
                        supabase
                            .from("CJEInscripciones")
                            .select("ninio_id,curso_id,inscrito")
                            .order("ninio_id")
                            .order("curso_id"),
                    ),
                ]);

                const enriched = await withPhotos(children);
                if (cancelled) return;

                setCursos(cs);
                setOptions(enriched);
                setNinios(
                    enriched.flatMap((n) =>
                        rows
                            .filter((i) => i.ninio_id === n.id)
                            .map((i) => ({
                                ...n,
                                curso_id: i.curso_id,
                                inscrito: i.inscrito,
                            })),
                    ),
                );
            } catch (e) {
                if (!cancelled)
                    setError(e.message || "No se pudieron cargar las fichas");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authenticated, version]);

    useEffect(() => {
        if (!authenticated) return;
        let cancelled = false;
        setLoadingDay(true);
        setDayError("");

        readAll(
            supabase
                .from("CJEAsistenciaNinios")
                .select("id")
                .eq("dia", dia)
                .order("id"),
        )
            .then((rows) => {
                if (!cancelled)
                    setAsistencia({ dia, ids: rows.map((r) => r.id) });
            })
            .catch((e) => {
                if (!cancelled)
                    setDayError(e.message || "No se pudo cargar la asistencia");
            })
            .finally(() => {
                if (!cancelled) setLoadingDay(false);
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated, dia, version, revision]);

    useEffect(() => {
        if (!authenticated) return;
        let cancelled = false;
        setLoadingMonth(true);
        setMonthError("");

        (async () => {
            try {
                const rows = await readAll(
                    supabase
                        .from("CJEAsistenciaNinios")
                        .select("id,dia")
                        .gte("dia", calendarMonth + "-01")
                        .lt(
                            "dia",
                            formatDateOnly(
                                new Date(
                                    Number(calendarMonth.slice(0, 4)),
                                    Number(calendarMonth.slice(5)),
                                    1,
                                ),
                            ),
                        )
                        .order("dia")
                        .order("id"),
                );

                const counts = {};
                for (const row of rows)
                    counts[row.dia] = (counts[row.dia] || 0) + 1;
                if (!cancelled) setMonthCounts(counts);
            } catch (e) {
                if (!cancelled) {
                    setMonthCounts({});
                    setMonthError(e.message || "No se pudo cargar el mes");
                }
            } finally {
                if (!cancelled) setLoadingMonth(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authenticated, calendarMonth, version, revision]);

    useEffect(() => {
        if (!authenticated) return;
        let timer,
            reloadAll = false,
            active = true;

        const refresh = (payload) => {
            if (!active) return;
            reloadAll ||= payload?.table !== "CJEAsistenciaNinios";
            clearTimeout(timer);
            timer = setTimeout(() => {
                if (reloadAll) setVersion((v) => v + 1);
                else setRevision((v) => v + 1);
                reloadAll = false;
            }, 200);
        };

        const channel = supabase.channel("cje-asistencia");
        for (const table of [
            "CJENinios",
            "CJEAsistenciaNinios",
            "CJEInscripciones",
            "CJECursos",
        ]) {
            channel.on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table,
                },
                refresh,
            );
        }

        channel.subscribe((status) => {
            if (!active) return;
            setRealtimeError(status !== "SUBSCRIBED");
            if (status === "SUBSCRIBED") refresh();
        });

        const interval = setInterval(
            () => {
                if (document.visibilityState === "visible") refresh();
            },
            5 * 60 * 1000,
        );

        window.addEventListener("focus", refresh);
        window.addEventListener("online", refresh);

        return () => {
            active = false;
            clearTimeout(timer);
            clearInterval(interval);
            window.removeEventListener("focus", refresh);
            window.removeEventListener("online", refresh);
            supabase.removeChannel(channel);
        };
    }, [authenticated]);

    const actuales = useMemo(
        () =>
            ninios
                .filter((n) => n.curso_id === curso?.id)
                .map((n) => ({
                    ...n,
                    attendedToday:
                        asistencia.dia === dia && asistencia.ids.includes(n.id),
                }))
                .sort((a, b) => a.nombre.localeCompare(b.nombre, "es")),
        [ninios, curso?.id, asistencia, dia],
    );

    const dayList = options
        .filter((n) => asistencia.dia === dia && asistencia.ids.includes(n.id))
        .map((n) => actuales.find((v) => v.id === n.id) || n)
        .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

    const duplicados = useMemo(() => {
        const groups = new Map(),
            ids = new Set();
        for (const n of options) {
            for (const key of [
                n.nombre
                    ?.trim()
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, ""),
                n.telefono?.replace(/\D/g, ""),
            ]) {
                if (!key) continue;
                if (groups.has(key)) {
                    ids.add(groups.get(key));
                    ids.add(n.id);
                } else groups.set(key, n.id);
            }
        }
        return ids;
    }, [options]);

    const fuse = useMemo(
        () =>
            new Fuse(
                options.map((n) => ({
                    ...n,
                    busqueda: [n.nombre, n.padres, n.telefono]
                        .filter(Boolean)
                        .join(" ")
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, ""),
                })),
                { keys: ["busqueda"], threshold: 0.35, ignoreLocation: true },
            ),
        [options],
    );

    const filtered = useMemo(() => {
        const matches = search.trim()
            ? fuse
                  .search(
                      search
                          .trim()
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, ""),
                  )
                  .map((r) => r.item)
            : [];

        const list = (
            search.trim()
                ? matches
                      .map((n) => actuales.find((v) => v.id === n.id))
                      .filter(Boolean)
                : actuales
        ).filter(
            (n) =>
                filtro === "todos" ||
                (filtro === "pendiente"
                    ? !n.inscrito
                    : filtro === "completo"
                      ? n.inscrito
                      : duplicados.has(n.id)),
        );

        return {
            notRegistered: list.filter((n) => !n.attendedToday),
            registered: list.filter((n) => n.attendedToday),
            previous: matches.filter(
                (n) => !actuales.some((v) => v.id === n.id),
            ),
        };
    }, [actuales, fuse, search, filtro, duplicados]);

    const handleDayChange = (date) => {
        if (!date || saving.current.size) return;
        setDia(formatDateOnly(date));
        setCalendarMonth(formatDateOnly(date).slice(0, 7));
    };

    const handleCursoChange = (id) => {
        const c = cursos.find((v) => v.id === id);
        if (!c) return;
        handleDayChange(
            parseYMD(
                todayDateOnly() < c.inicio
                    ? c.inicio
                    : c.fin && todayDateOnly() > c.fin
                      ? c.fin
                      : todayDateOnly(),
            ),
        );
    };

    const markAttendance = async (n, asiste = true) => {
        if (!ready || saving.current.has(n.id) || dia > todayDateOnly()) return;
        saving.current.add(n.id);
        setSavingIds([...saving.current]);

        try {
            const { error: err } = asiste
                ? await supabase
                      .from("CJEAsistenciaNinios")
                      .upsert(
                          { id: n.id, dia },
                          { onConflict: "id,dia", ignoreDuplicates: true },
                      )
                : await supabase
                      .from("CJEAsistenciaNinios")
                      .delete()
                      .eq("id", n.id)
                      .eq("dia", dia);

            if (err) throw err;
            setToast({
                open: true,
                msg: `${n.nombre}: ${asiste ? "asistencia marcada" : "asistencia retirada"} · ${dia}`,
                sev: "success",
            });
        } catch (e) {
            setToast({
                open: true,
                msg: e.message || "No se pudo guardar",
                sev: "error",
            });
        } finally {
            setLoadingDay(true);
            setRevision((v) => v + 1);
            saving.current.delete(n.id);
            setSavingIds([...saving.current]);
        }
    };

    const handleSaved = (done) => {
        setVersion((v) => v + 1);
        if (done)
            setToast({ open: true, msg: "Ficha guardada", sev: "success" });
    };

    const handlePickPhoto = (n) => {
        setPhotoTarget(n);
        photoPath.current = null;
        fileInputRef.current?.click();
    };

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (f) {
            setRawFile(f);
            setCropOpen(true);
        }
    };

    const handleConfirmCropped = async (finalFile) => {
        if (!finalFile || !photoTarget) return;
        photoPath.current ||=
            "Ninios_" + photoTarget.id + "_" + crypto.randomUUID() + ".jpg";

        const { error: uploadError } = await supabase.storage
            .from("NiniosFotos")
            .upload(photoPath.current, finalFile, {
                upsert: true,
                contentType: "image/jpeg",
            });
        if (uploadError) throw uploadError;

        const { error: photoError } = await supabase
            .from("CJENinios")
            .update({ foto_path: photoPath.current })
            .eq("id", photoTarget.id)
            .select("id")
            .single();
        if (photoError) throw photoError;

        setCropOpen(false);
        setRawFile(null);
        setPhotoTarget(null);
        setVersion((v) => v + 1);
        setToast({ open: true, msg: "Foto actualizada", sev: "success" });
    };

    const renderGrid = (items, recuperar = false) => (
        <Grid
            container
            rowSpacing={1}
            columnSpacing={1}
            columns={{ xs: 2, sm: 3, md: 4, lg: 5 }}
            sx={{
                width: "100%",
                mx: 0,
                "&::before, &::after": { content: "none" },
            }}
        >
            {items.map((n) => (
                <Grid
                    key={dia + ":" + n.id}
                    size={1}
                    sx={{ minWidth: 0, display: "flex" }}
                >
                    <NinioCard
                        ninio={n}
                        onMarkAttend={markAttendance}
                        onUndoAttend={(v) => markAttendance(v, false)}
                        onInfo={setInfoTarget}
                        onPickPhoto={handlePickPhoto}
                        busy={savingIds.includes(n.id)}
                        onRecover={
                            recuperar
                                ? (n) => {
                                      setRecoverTarget(n);
                                      setCreateOpen(true);
                                  }
                                : undefined
                        }
                        disabled={
                            !ready ||
                            (!recuperar &&
                                (loadingDay || dia > todayDateOnly()))
                        }
                    />
                </Grid>
            ))}
        </Grid>
    );

    if (!authenticated)
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    bgcolor: "background.default",
                    p: 2,
                }}
            >
                <Typography variant="h6">Control de asistencia</Typography>
                <Typography variant="h5">Acceso restringido</Typography>
                <TextField
                    label="Contraseña"
                    type={showPassword ? "text" : "password"}
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (
                            e.key === "Enter" &&
                            passwordInput === correctPassword
                        ) {
                            setAuthenticated(true);
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Mostrar contraseña"
                                    edge="end"
                                    onClick={() => setShowPassword((v) => !v)}
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    onClick={() => {
                        if (passwordInput === correctPassword)
                            setAuthenticated(true);
                        else alert("Contraseña incorrecta");
                    }}
                >
                    Entrar
                </Button>
            </Box>
        );

    return (
        <Box sx={{ pb: 12 }}>
            <AppBar
                position="sticky"
                color={dia === todayDateOnly() ? "primary" : "warning"}
            >
                <Toolbar sx={{ gap: 1 }}>
                    <Box
                        component="img"
                        src={logoCJE}
                        alt="CJE"
                        sx={{
                            height: 32,
                            maxHeight: "100%",
                            width: "auto",
                            mr: 1.5,
                            display: "block",
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{ display: { xs: "none", sm: "block" } }}
                        >
                            Asistencia
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                            color="inherit"
                            onClick={() => setQrOpen("instagram")}
                            aria-label="Abrir QR de Instagram"
                        >
                            <InstagramIcon />
                        </IconButton>

                        <IconButton
                            color="inherit"
                            onClick={() => setQrOpen("whatsapp")}
                            aria-label="Abrir QR de WhatsApp"
                        >
                            <WhatsAppIcon />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            onClick={() => setQrOpen(true)}
                            aria-label="Abrir QR del formulario"
                        >
                            <QrCode2Icon />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            onClick={() => setVersion((v) => v + 1)}
                            aria-label="Actualizar"
                        >
                            <CachedIcon />
                        </IconButton>
                        <Chip
                            variant="filled"
                            color="default"
                            label={dia}
                            sx={{ fontStyle: "italic", color: "white" }}
                        />
                    </Box>
                    {dia !== todayDateOnly() && (
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ ml: 1, color: "white", borderColor: "white" }}
                            disabled={!!savingIds.length}
                            onClick={() =>
                                handleDayChange(parseYMD(todayDateOnly()))
                            }
                        >
                            hoy
                        </Button>
                    )}
                </Toolbar>
            </AppBar>

            <Stack spacing={2} sx={{ p: 2 }}>
                <Typography sx={{ px: 2, pb: 1 }}>
                    {dia === todayDateOnly()
                        ? "Hoy"
                        : parseYMD(dia).toLocaleDateString("es-ES")}
                    : {ready ? dayList.length : "…"} asistentes
                    {" · "}
                    {loading || error ? "…" : actuales.length} inscritos este
                    curso
                    {" · "}
                    {loading || error
                        ? "…"
                        : actuales.filter((n) => !n.inscrito).length}{" "}
                    con inscripción pendiente
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <TextField
                        select
                        size="small"
                        label="Curso"
                        value={curso?.id || ""}
                        sx={{ minWidth: 150 }}
                        disabled={loading || !!error || !!savingIds.length}
                        onChange={(e) => handleCursoChange(e.target.value)}
                    >
                        {cursos.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.id}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="outlined"
                        disabled={!curso || loading || !!error}
                        onClick={() => {
                            setLinkTarget(null);
                            setPickerOpen(true);
                        }}
                    >
                        Traer de otro curso
                    </Button>
                    {(loading || loadingDay) && (
                        <Stack alignItems="center">
                            <CircularProgress />
                        </Stack>
                    )}
                </Stack>

                {dia > todayDateOnly() && (
                    <Alert severity="info">
                        Puedes preparar las inscripciones de {curso?.id}. La
                        asistencia se podrá marcar desde el {dia}.
                    </Alert>
                )}
                {!loading && !error && !curso && (
                    <Alert severity="warning">
                        No hay un curso configurado para esta fecha.
                    </Alert>
                )}
                {realtimeError && (
                    <Alert severity="warning">
                        No está conectada la actualización en directo. Pulsa
                        Actualizar para comprobar los últimos cambios.
                    </Alert>
                )}
                {error && <Alert severity="error">{error}</Alert>}
                {dayError && <Alert severity="error">{dayError}</Alert>}

                <TextField
                    fullWidth
                    placeholder="Buscar por nombre..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    select
                    size="small"
                    label="Filtro"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                >
                    {[
                        ["todos", "Todos"],
                        ["pendiente", "Inscripción pendiente"],
                        ["completo", "Inscripción confirmada"],
                        ["duplicados", "Posibles duplicados"],
                    ].map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                {filtro === "duplicados" && (
                    <Alert severity="info">
                        Coinciden en nombre o teléfono. Pueden ser hermanos:
                        revisa las fichas antes de enlazarlas.
                    </Alert>
                )}
            </Stack>

            <Box sx={{ p: 2, pt: 0 }}>
                {ready && curso && (
                    <>
                        {!actuales.length && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Este curso aún no tiene niños. Puedes crearlos o
                                recuperar fichas anteriores.
                            </Alert>
                        )}
                        {!!actuales.length &&
                            !filtered.notRegistered.length &&
                            !filtered.registered.length && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    No hay resultados con esta búsqueda y
                                    filtro.
                                </Alert>
                            )}

                        <Accordion
                            expanded={expandA}
                            onChange={(_, v) => setExpandA(v)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        left: 8,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 2,
                                        bgcolor: "warning.main",
                                        color: "common.white",
                                        fontSize: 16,
                                        fontWeight: 700,
                                        letterSpacing: 0.3,
                                        textTransform: "uppercase",
                                        boxShadow: 2,
                                    }}
                                >
                                    {"Pendientes " +
                                        filtered.notRegistered.length}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filtered.notRegistered.length ? (
                                    renderGrid(filtered.notRegistered)
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {search.trim() || filtro !== "todos"
                                            ? "No hay pendientes con esta búsqueda y filtro."
                                            : "Todo listo aquí 🎉"}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            expanded={expandB}
                            onChange={(_, v) => setExpandB(v)}
                            sx={{ mt: 1 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        left: 8,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 2,
                                        bgcolor: "success.main",
                                        color: "common.white",
                                        fontSize: 16,
                                        fontWeight: 700,
                                        letterSpacing: 0.3,
                                        textTransform: "uppercase",
                                        boxShadow: 2,
                                    }}
                                >
                                    {"Asistieron " + filtered.registered.length}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filtered.registered.length ? (
                                    renderGrid(filtered.registered)
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {search.trim() || filtro !== "todos"
                                            ? "No hay asistentes con esta búsqueda y filtro."
                                            : "Aún no hay asistencias marcadas."}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        {!!search.trim() && (
                            <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            left: 8,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 2,
                                            bgcolor: "primary.main",
                                            color: "common.white",
                                            fontSize: 16,
                                            fontWeight: 700,
                                            letterSpacing: 0.3,
                                            textTransform: "uppercase",
                                            boxShadow: 2,
                                        }}
                                    >
                                        {"Cursos anteriores " +
                                            filtered.previous.length}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {filtered.previous.length ? (
                                        renderGrid(filtered.previous, true)
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No hay coincidencias fuera de este
                                            curso.
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </>
                )}

                <Box sx={{ mt: 2 }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems="stretch"
                    >
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                p: 1,
                            }}
                        >
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={esLocale}
                            >
                                <DateCalendar
                                    value={calendarDate}
                                    onChange={handleDayChange}
                                    maxDate={parseYMD(todayDateOnly())}
                                    disabled={!!savingIds.length}
                                    onMonthChange={(d) =>
                                        setCalendarMonth(
                                            formatDateOnly(d).slice(0, 7),
                                        )
                                    }
                                    loading={loadingMonth}
                                    renderLoading={() => (
                                        <DayCalendarSkeleton />
                                    )}
                                    slots={{ day: ServerDay }}
                                    slotProps={{ day: { monthCounts } }}
                                />
                            </LocalizationProvider>
                            {monthError && (
                                <Alert severity="error">{monthError}</Alert>
                            )}
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                p: 2,
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 1 }}
                            >
                                <CalendarMonthIcon />
                                <Typography variant="h6">{dia}</Typography>
                                <Chip
                                    color="primary"
                                    label={
                                        "Total: " +
                                        (ready ? dayList.length : "…") +
                                        " niños"
                                    }
                                />
                            </Stack>
                            <Divider sx={{ mb: 1 }} />

                            {loading || loadingDay ? (
                                <Stack alignItems="center" sx={{ py: 4 }}>
                                    <CircularProgress />
                                </Stack>
                            ) : error || dayError ? (
                                <Alert severity="error">
                                    {dayError || error}
                                </Alert>
                            ) : (
                                ready &&
                                (dayList.length ? (
                                    <List dense>
                                        {dayList.map((n) => (
                                            <ListItem
                                                key={n.id}
                                                onClick={() => setInfoTarget(n)}
                                                role="button"
                                                tabIndex={0}
                                                sx={{ cursor: "pointer" }}
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                    ) {
                                                        e.preventDefault();
                                                        setInfoTarget(n);
                                                    }
                                                }}
                                                secondaryAction={
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        aria-label={`Quitar asistencia de ${n.nombre}`}
                                                        disabled={
                                                            !ready ||
                                                            loadingDay ||
                                                            savingIds.includes(
                                                                n.id,
                                                            ) ||
                                                            dia >
                                                                todayDateOnly()
                                                        }
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (
                                                                window.confirm(
                                                                    `¿Quitar la asistencia de ${n.nombre} del día ${dia}?`,
                                                                )
                                                            ) {
                                                                markAttendance(
                                                                    n,
                                                                    false,
                                                                );
                                                            }
                                                        }}
                                                        onKeyDown={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemAvatar>
                                                    <Avatar src={n.signedUrl}>
                                                        {(n.nombre ||
                                                            "N")[0]?.toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={n.nombre}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No hay asistencias ese día.
                                    </Typography>
                                ))
                            )}
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ mt: 3, display: "flex", justifyContent: "left" }}>
                    <Button
                        variant="outlined"
                        startIcon={<CalendarMonthIcon />}
                        disabled={!!savingIds.length}
                        onClick={() => {
                            setEditOtherTemp(parseYMD(dia));
                            setEditOtherOpen(true);
                        }}
                    >
                        Editar otro día
                    </Button>
                </Box>
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={onFileChange}
            />
            <PhotoCropDialog
                open={cropOpen}
                rawFile={rawFile}
                onClose={() => setCropOpen(false)}
                onConfirm={handleConfirmCropped}
            />

            <CreateNinioDialog
                open={createOpen}
                ninio={recoverTarget}
                curso={curso}
                dia={dia}
                options={options}
                onClose={() => {
                    setCreateOpen(false);
                    setRecoverTarget(null);
                }}
                onSaved={handleSaved}
            />
            <EditNinioDialog
                open={!!editTarget}
                ninio={editTarget}
                curso={curso}
                dia={dia}
                onClose={() => setEditTarget(null)}
                onSaved={handleSaved}
            />
            <NinioInfoDialog
                ninio={
                    infoTarget &&
                    (ninios.find(
                        (n) =>
                            n.id === infoTarget.id &&
                            n.curso_id === infoTarget.curso_id,
                    ) ||
                        infoTarget)
                }
                cursos={cursos}
                version={version + ":" + revision}
                onSaved={handleSaved}
                onClose={() => setInfoTarget(null)}
                onEdit={(n) => {
                    setInfoTarget(null);
                    setEditTarget(n);
                }}
                onLink={(n) => {
                    setInfoTarget(null);
                    setLinkTarget(n);
                    setPickerOpen(true);
                }}
                onDay={(d) => {
                    setInfoTarget(null);
                    handleDayChange(parseYMD(d));
                }}
                onAddCourse={(n, id) => {
                    setInfoTarget(null);
                    handleCursoChange(id);
                    setRecoverTarget(n);
                    setCreateOpen(true);
                }}
            />
            <NinioPickerDialog
                open={pickerOpen}
                ninio={linkTarget}
                curso={curso}
                options={options.filter((n) =>
                    linkTarget
                        ? n.id !== linkTarget.id
                        : !actuales.some((v) => v.id === n.id),
                )}
                onClose={() => {
                    setPickerOpen(false);
                    setLinkTarget(null);
                }}
                onRecover={(n) => {
                    setPickerOpen(false);
                    setRecoverTarget(n);
                    setCreateOpen(true);
                }}
                onLinked={() => {
                    setVersion((v) => v + 1);
                    setToast({
                        open: true,
                        msg: "Fichas enlazadas",
                        sev: "success",
                    });
                }}
            />

            <Fab
                color="secondary"
                aria-label="Crear niño"
                sx={{ position: "fixed", bottom: 16, right: 16 }}
                disabled={!curso || loading || !!error}
                onClick={() => {
                    setRecoverTarget(null);
                    setCreateOpen(true);
                }}
            >
                <AddIcon />
            </Fab>

            <Dialog
                open={!!qrOpen}
                onClose={() => setQrOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{qr.titulo}</DialogTitle>
                <DialogContent>
                    {qr.url ? (
                        <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
                            <Box
                                component="img"
                                alt={`QR · ${qr.titulo}`}
                                sx={{
                                    width: "100%",
                                    maxWidth: 320,
                                    aspectRatio: "1 / 1",
                                    display: "block",
                                }}
                                src={
                                    "https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=" +
                                    encodeURIComponent(qr.url)
                                }
                            />
                        </Stack>
                    ) : (
                        <Alert severity="info">
                            Todavía no se ha configurado el formulario de este
                            curso.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    {qr.url && (
                        <Button
                            component="a"
                            href={qr.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                        >
                            {qrOpen === "instagram"
                                ? "Abrir Instagram"
                                : qrOpen === "whatsapp"
                                  ? "Abrir WhatsApp"
                                  : "Abrir formulario"}
                        </Button>
                    )}
                    <Button onClick={() => setQrOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={editOtherOpen}
                onClose={() => setEditOtherOpen(false)}
                fullWidth
            >
                <DialogTitle>Selecciona el día a editar</DialogTitle>
                <DialogContent>
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={esLocale}
                    >
                        <DateCalendar
                            value={editOtherTemp}
                            onChange={setEditOtherTemp}
                            maxDate={parseYMD(todayDateOnly())}
                        />
                    </LocalizationProvider>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Día seleccionado:{" "}
                        {editOtherTemp ? formatDateOnly(editOtherTemp) : "—"}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOtherOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!editOtherTemp || !!savingIds.length}
                        onClick={() => {
                            handleDayChange(editOtherTemp);
                            setEditOtherOpen(false);
                        }}
                    >
                        Usar esta fecha
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={toast.sev === "error" ? 5000 : 2500}
                onClose={() => setToast((v) => ({ ...v, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={toast.sev}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CJEAsistencia;
